<template>
  <v-dialog v-model="open" persistent max-width="450">
    <v-card>
      <v-card-title>Payment Method</v-card-title>

      <v-card-text>
        <template>
          <v-select dense outlined :items="payment_methods" :disabled="getIsUpdateAccountLoading || noMatch" label="Payment Method" required item-text="text" item-value="value" v-model="payment_method" :error-messages="paymentMethodError" @input="$v.payment_method.$touch()" @blur="$v.payment_method.$touch()" class="mb-0"></v-select>
            
          <template v-if="payment_method === 'DD'">
            <v-text-field dense outlined label="Account holder" v-model="payee_account_name" :disabled="getIsUpdateAccountLoading || noMatch" :error-messages="payeeAccountNameNoError" @input="$v.payee_account_name.$touch()" @blur="$v.payee_account_name.$touch()" class="mb-0"></v-text-field>

            <v-text-field dense outlined label="Sort Code" v-model="sort_code" :disabled="getIsUpdateAccountLoading || noMatch" :error-messages="sortCodeError" @input="$v.sort_code.$touch()" @blur="$v.sort_code.$touch()" class="mb-0"></v-text-field>
          
            <v-text-field dense outlined label="Account Number" v-model="bank_acc_no" :disabled="getIsUpdateAccountLoading || noMatch" :error-messages="bankAccNoError" @input="$v.bank_acc_no.$touch()" @blur="$v.bank_acc_no.$touch()" class="mb-0"></v-text-field>

            <v-select dense outlined :items="payeeAccountTypes" :disabled="getIsUpdateAccountLoading || noMatch" label="Account type" required item-text="text" item-value="value" v-model="payee_account_type" :error-messages="payeeAccountTypeError" @input="$v.payee_account_type.$touch()" @blur="$v.payee_account_type.$touch()" class="mb-0"></v-select>
          </template>
        </template>
      </v-card-text>
      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" small :disabled="getIsUpdateAccountLoading || noMatch" :loading="getIsUpdateAccountLoading" @click="save">Save</v-btn>
        <v-btn color="primary" outlined small :disabled="getIsUpdateAccountLoading" @click="closeDialog">cancel</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { validationMixin } from "vuelidate";
import { required, numeric, minLength, maxLength } from "vuelidate/lib/validators";

export default {
  mixins: [validationMixin],

  validations() {
    if(this.payment_method && this.payment_method !== "DD") {
      return {
        payment_method: { required },
      }
    }

    return {
      payment_method: { required },
      sort_code: { required, numeric, minLength: minLength(6), maxLength: maxLength(6) },
      bank_acc_no: { required, numeric, minLength: minLength(8), maxLength: maxLength(8) },
      payee_account_name: { required },
      payee_account_type: { required },
    }
  },

  props: {
    open: Boolean,
    account: Object
  },

  data: () => ({
    payment_method: "",
    sort_code: "",
    bank_acc_no: "",
    payee_account_name: "",
    payee_account_type: "",
    payment_methods: [
      {value:"AN",text:"Annual"},
      {value:"DD",text:"Direct Debit"},
      {value:"MP",text:"Manual Payer"},
      {value:"CQ",text:"Cheque"},
      {value:"PP",text:"Paypal"},
      {value:"SO",text:"Standing Order"},
      {value:"NO",text:"No Method Chosen"}
    ],
    payeeAccountTypes: [
      {value:"BUSINESS",text:"Business"},
      {value:"PERSONAL",text:"Personal"}
    ]
  }),

  computed: {
    ...mapGetters("accounts", ["getCurrentAccount", "getIsUpdateAccountLoading", "getIsUpdateAccountError"]),

    paymentMethodError() {
      const errors = [];
      if (!this.$v.payment_method.$dirty) return errors;
      !this.$v.payment_method.required && errors.push("Payment method is required");
      return errors;
    },

    sortCodeError() {
      const errors = [];
      if (!this.$v.sort_code.$dirty) return errors;
      !this.$v.sort_code.required && errors.push("Sort code is required");
      !this.$v.sort_code.numeric && errors.push("Sort code must be numeric");
      !this.$v.sort_code.minLength && errors.push("Sort code must be a 6-digit number");
      !this.$v.sort_code.maxLength && errors.push("Sort code must be a 6-digit number");
      return errors;
    },

    bankAccNoError() {
      const errors = [];
      if (!this.$v.bank_acc_no.$dirty) return errors;
      !this.$v.bank_acc_no.required && errors.push("Account number is required");
      !this.$v.bank_acc_no.numeric && errors.push("Account number must be numeric");
      !this.$v.bank_acc_no.minLength && errors.push("Account number must be a 8-digit number");
      !this.$v.bank_acc_no.maxLength && errors.push("Account number must be a 8-digit number");
      return errors;
    },

    payeeAccountNameNoError() {
      const errors = [];
      if (!this.$v.payee_account_name.$dirty) return errors;
      !this.$v.payee_account_name.required && errors.push("Account holder name is a required field");
      return errors;
    },

    payeeAccountTypeError() {
      const errors = [];
      if (!this.$v.payee_account_type.$dirty) return errors;
      !this.$v.payee_account_type.required && errors.push("Account type is a required field");
      return errors;
    },

    noMatch() {
      return this.getCurrentAccount.payee_confirm_status === "No Match" && this.getCurrentAccount.payee_confirm_attempt === 3;
    }
  },

  watch: {
    account(newVal) {
      if (newVal) {
        this.sort_code = this.account.sort_code;
        this.bank_acc_no = this.account.bank_acc_no;
        this.payment_method = this.account.payment_method;
        this.payee_account_name = this.account.payee_account_name;
        this.payee_account_type = this.account.payee_account_type;
      }
    }
  },

  methods: {
    ...mapActions("accounts", ["updateAccount"]),
    ...mapActions("alerts", ["setAlert"]),

    async save() {
      this.$v.$touch();

      if(!this.$v.$invalid) {
        let data = {  
          payment_method: this.payment_method
        };
        
        if (this.payment_method === "DD") {
          data.sort_code = this.sort_code;
          data.bank_acc_no = this.bank_acc_no;
          data.payee_account_name = this.payee_account_name;
          data.payee_account_type = this.payee_account_type;
        }

        await this.updateAccount({id: this.account.id, data});
        if(!this.getIsUpdateAccountError) {
          this.closeDialog();
        }
      }
    },

    setForm() {
      this.sort_code = this.account.sort_code;
      this.bank_acc_no = this.account.bank_acc_no;
      this.payment_method = this.account.payment_method;
      this.payee_account_name = this.account.payee_account_name;
      this.payee_account_type = this.account.payee_account_type;
    },

    closeDialog(){
      this.$emit('close-dialog');
      this.$v.$reset();
      this.setForm();
    },
  }
};
</script>

<style lang="scss" scoped>
</style>
